import { css } from 'styled-components';
import type { Theme } from './theme';

// MARK: - Common Typography Mixins
export const typographyMixin = {
  // Primary text styles
  primary: css`
    font-family: ${({ theme }: { theme: Theme }) => theme.typography.fontFamily.primary};
    font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeight.normal};
    letter-spacing: ${({ theme }: { theme: Theme }) => theme.typography.letterSpacing.wide};
    text-transform: ${({ theme }: { theme: Theme }) => theme.typography.textTransform.uppercase};
  `,
  
  // Secondary text styles
  secondary: css`
    font-family: ${({ theme }: { theme: Theme }) => theme.typography.fontFamily.secondary};
    font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeight.normal};
    letter-spacing: ${({ theme }: { theme: Theme }) => theme.typography.letterSpacing.normal};
  `,
  
  // Heading styles
  heading: css`
    font-family: ${({ theme }: { theme: Theme }) => theme.typography.fontFamily.primary};
    font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeight.semibold};
    letter-spacing: ${({ theme }: { theme: Theme }) => theme.typography.letterSpacing.wide};
    text-transform: ${({ theme }: { theme: Theme }) => theme.typography.textTransform.uppercase};
  `,
  
  // Button text styles
  button: css`
    font-family: ${({ theme }: { theme: Theme }) => theme.typography.fontFamily.primary};
    font-weight: ${({ theme }: { theme: Theme }) => theme.typography.fontWeight.normal};
    letter-spacing: ${({ theme }: { theme: Theme }) => theme.typography.letterSpacing.wide};
    text-transform: ${({ theme }: { theme: Theme }) => theme.typography.textTransform.uppercase};
  `,
};

// MARK: - Common Color Mixins
export const colorMixin = {
  // Text colors
  textPrimary: css`
    color: ${({ theme }: { theme: Theme }) => theme.colors.text.primary};
  `,
  
  textSecondary: css`
    color: ${({ theme }: { theme: Theme }) => theme.colors.text.secondary};
  `,
  
  textMuted: css`
    color: ${({ theme }: { theme: Theme }) => theme.colors.text.muted};
  `,
  
  textError: css`
    color: ${({ theme }: { theme: Theme }) => theme.colors.text.error};
  `,
  
  // Background colors
  bgPrimary: css`
    background-color: ${({ theme }: { theme: Theme }) => theme.colors.background.primary};
  `,
  
  bgSecondary: css`
    background-color: ${({ theme }: { theme: Theme }) => theme.colors.background.secondary};
  `,
  
  // Interactive colors
  buttonPrimary: css`
    background-color: ${({ theme }: { theme: Theme }) => theme.colors.interactive.button.background};
    color: ${({ theme }: { theme: Theme }) => theme.colors.interactive.button.text};
  `,
  
  buttonHover: css`
    background-color: ${({ theme }: { theme: Theme }) => theme.colors.interactive.button.hover.background};
    color: ${({ theme }: { theme: Theme }) => theme.colors.interactive.button.hover.text};
  `,
};

// MARK: - Common Spacing Mixins
export const spacingMixin = {
  // Padding
  padding: {
    xs: css`padding: ${({ theme }: { theme: Theme }) => theme.spacing.xs};`,
    sm: css`padding: ${({ theme }: { theme: Theme }) => theme.spacing.sm};`,
    md: css`padding: ${({ theme }: { theme: Theme }) => theme.spacing.md};`,
    lg: css`padding: ${({ theme }: { theme: Theme }) => theme.spacing.lg};`,
    xl: css`padding: ${({ theme }: { theme: Theme }) => theme.spacing.xl};`,
  },
  
  // Margin
  margin: {
    xs: css`margin: ${({ theme }: { theme: Theme }) => theme.spacing.xs};`,
    sm: css`margin: ${({ theme }: { theme: Theme }) => theme.spacing.sm};`,
    md: css`margin: ${({ theme }: { theme: Theme }) => theme.spacing.md};`,
    lg: css`margin: ${({ theme }: { theme: Theme }) => theme.spacing.lg};`,
    xl: css`margin: ${({ theme }: { theme: Theme }) => theme.spacing.xl};`,
  },
  
  // Gap
  gap: {
    xs: css`gap: ${({ theme }: { theme: Theme }) => theme.spacing.xs};`,
    sm: css`gap: ${({ theme }: { theme: Theme }) => theme.spacing.sm};`,
    md: css`gap: ${({ theme }: { theme: Theme }) => theme.spacing.md};`,
    lg: css`gap: ${({ theme }: { theme: Theme }) => theme.spacing.lg};`,
    xl: css`gap: ${({ theme }: { theme: Theme }) => theme.spacing.xl};`,
  },
};

// MARK: - Common Border Mixins
export const borderMixin = {
  // Border radius
  radius: {
    none: css`border-radius: ${({ theme }: { theme: Theme }) => theme.borderRadius.none};`,
    sm: css`border-radius: ${({ theme }: { theme: Theme }) => theme.borderRadius.sm};`,
    md: css`border-radius: ${({ theme }: { theme: Theme }) => theme.borderRadius.md};`,
    lg: css`border-radius: ${({ theme }: { theme: Theme }) => theme.borderRadius.lg};`,
    xl: css`border-radius: ${({ theme }: { theme: Theme }) => theme.borderRadius.xl};`,
    full: css`border-radius: ${({ theme }: { theme: Theme }) => theme.borderRadius.full};`,
  },
  
  // Border colors
  borderPrimary: css`
    border-color: ${({ theme }: { theme: Theme }) => theme.colors.primary};
  `,
  
  borderSecondary: css`
    border-color: ${({ theme }: { theme: Theme }) => theme.colors.secondary};
  `,
};

// MARK: - Common Shadow Mixins
export const shadowMixin = {
  sm: css`box-shadow: ${({ theme }: { theme: Theme }) => theme.shadows.sm};`,
  md: css`box-shadow: ${({ theme }: { theme: Theme }) => theme.shadows.md};`,
  lg: css`box-shadow: ${({ theme }: { theme: Theme }) => theme.shadows.lg};`,
  xl: css`box-shadow: ${({ theme }: { theme: Theme }) => theme.shadows.xl};`,
};

// MARK: - Common Transition Mixins
export const transitionMixin = {
  fast: css`transition: ${({ theme }: { theme: Theme }) => theme.transitions.fast};`,
  normal: css`transition: ${({ theme }: { theme: Theme }) => theme.transitions.normal};`,
  slow: css`transition: ${({ theme }: { theme: Theme }) => theme.transitions.slow};`,
};

// MARK: - Common Component Mixins
export const componentMixin = {
  // Input styles
  input: css`
    ${typographyMixin.primary}
    ${colorMixin.textPrimary}
    border: none;
    border-bottom: 0.5px solid ${({ theme }: { theme: Theme }) => theme.colors.text.muted};
    background: none;
    outline: none;
    width: 100%;
    
    &::placeholder {
      ${colorMixin.textSecondary}
    }
    
    &:hover, &:focus {
      border-bottom-color: ${({ theme }: { theme: Theme }) => theme.colors.primary};
    }
  `,
  
  // Button styles
  button: css`
    ${typographyMixin.button}
    ${colorMixin.buttonPrimary}
    ${spacingMixin.padding.md}
    ${borderMixin.radius.md}
    ${transitionMixin.normal}
    border: none;
    cursor: pointer;
    width: 100%;
    
    &:hover {
      ${colorMixin.buttonHover}
    }
    
    &:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }
  `,
  
  // Card styles
  card: css`
    ${colorMixin.bgPrimary}
    ${shadowMixin.lg}
    ${borderMixin.radius.md}
    ${spacingMixin.padding.lg}
  `,
};

// MARK: - Utility Functions
export const getResponsiveValue = (
  mobile: string,
  tablet: string = mobile,
  desktop: string = tablet
) => css`
  ${mobile}
  
  @media (min-width: 768px) {
    ${tablet}
  }
  
  @media (min-width: 1024px) {
    ${desktop}
  }
`;

export const getHoverStyles = (styles: ReturnType<typeof css>) => css`
  &:hover {
    ${styles}
  }
`;

export const getFocusStyles = (styles: ReturnType<typeof css>) => css`
  &:focus {
    ${styles}
  }
`;

export const getDisabledStyles = (styles: ReturnType<typeof css>) => css`
  &:disabled {
    ${styles}
  }
`; 